/**
 * lib/api/masters.ts — Stage FW-2a + FW-5
 *
 * Master data fetching from backend: Stations, Hotels, and Roster.
 *
 * Key responsibilities:
 * 1. fetchAllPages<T>() — pagination helper for DRF PageNumberPagination
 *    (follows `next` links, returns flat array). For bounded master data only.
 *    Crew (large, PII-sensitive) will need a different pattern later.
 * 2. mapHotelResponse() — pure mapper that converts DRF DecimalField strings
 *    to numbers (distance_from_airport_km, contracted_rate_single, etc.)
 * 3. fetchStations() — no mapping needed, fields match frontend Station type
 * 4. fetchHotels() — applies decimal→number mapper
 * 5. FW-5: fetchRosterPublications() — maps tenant→tenant_id
 * 6. FW-5: fetchRosterLinesPage() — maps crew→crew_id, publication→publication_id, etc.
 */

import { get, getBlob, patch, post } from './client';
import type { ProjectionBulkScope } from '@/lib/projection/bulk-scope';
import type { AllocatorRule, Station, Hotel, HotelPriority, HotelBrandGroup, SecurityStatus, RosterPublication, RosterLine, RosterLineType, RosterLineStatus, RosterLineStatusDetail, RosterType, StayType, CrewRank, CrewListItem, Rank, HomeBaseOverrideOutcome, TenantSettings, SystemConfig, NotificationBand, Branding, InventoryAlert, InventoryAlertType, InventoryAlertStatus, UnallottedReport, BookingMatrixKey } from '@/lib/types';

/**
 * DRF PageNumberPagination envelope.
 * PAGE_SIZE=50 on backend.
 */
interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Fetch all pages from a paginated endpoint.
 *
 * page_size is owned exclusively by this helper (via the `pageSize` param) —
 * callers must NOT append `page_size` to `path` themselves. Doing so used to
 * produce a duplicated query param; Django's QueryDict.get() resolves that to
 * the LAST occurrence, which silently overrode whatever the caller intended.
 * Callers needing a non-default page size pass it as `pageSize`, not in the path.
 *
 * IMPORTANT: This helper is for BOUNDED master data (stations, hotels).
 * Crew data (250+, PII-sensitive) will need a different pattern with
 * cursor-based pagination or streaming. Do NOT generalize this for huge data.
 *
 * Page 1 is always fetched alone — it's the only way to learn `count` and the
 * actual page size the server used (which may be less than requested; e.g.
 * /stations/ and /hotels/ clamp to a max_page_size of 100). Pages 2..N are
 * then fetched with Promise.all — concurrently, not one round trip at a time —
 * and concatenated back in page order (not resolution order).
 *
 * CRITICAL: Handles mid-pagination count changes gracefully (e.g., when rows are
 * cancelled/modified during live booking churn). If a page fetch returns 404
 * "Invalid page", that single page's data is dropped but every other
 * concurrently-fetched page is still returned — unlike the old sequential
 * while-loop, a 404 on page 4 no longer loses page 5's data too.
 */
export async function fetchAllPages<T>(path: string, pageSize = 1000): Promise<T[]> {
  // Ask for a large page rather than accepting the server default. At 50 rows a
  // page, 2,000 projection entries meant 43 sequential round trips - the page felt
  // slow purely from request latency, not data volume. The server caps this
  // (BookingPageNumberPagination max_page_size = 1000), so an oversized request is
  // clamped rather than refused.
  const sep = path.includes('?') ? '&' : '?';
  const firstPath = pageSize ? `${path}${sep}page_size=${pageSize}` : path;

  const first: PaginatedResponse<T> = await get<PaginatedResponse<T>>(firstPath);
  const results: T[] = [...first.results];

  // No further pages (or a degenerate empty-but-has-next response, which
  // shouldn't happen from DRF but would otherwise divide by zero below).
  if (!first.next || first.results.length === 0) {
    return results;
  }

  // Derive page 2..N URLs from the `next` link so every other query param
  // (the page_size actually applied, ?run= filters, etc.) survives — only the
  // `page` number is swapped per request. Total pages is computed from the
  // ACTUAL page size the server returned (first.results.length), not the
  // requested `pageSize`, so this stays correct even when the server clamps
  // the request (stations/hotels max_page_size=100).
  const nextUrl = new URL(first.next);
  const totalPages = Math.ceil(first.count / first.results.length);

  const pageFetches: Promise<{ page: number; results: T[] } | null>[] = [];
  for (let page = 2; page <= totalPages; page++) {
    const url = new URL(nextUrl.toString());
    url.searchParams.set('page', String(page));
    const pagePath = url.pathname + url.search;

    pageFetches.push(
      get<PaginatedResponse<T>>(pagePath)
        .then((response) => ({ page, results: response.results }))
        .catch((error) => {
          // If the error indicates an invalid page (404), drop just this page
          // and keep the rest — this can occur when the total count shrinks
          // between page requests (rows cancelled/changed mid-pagination).
          const errorMessage = error instanceof Error ? error.message : String(error);
          if (errorMessage.includes('Invalid page') || errorMessage.includes('404')) {
            return null;
          }
          // For any other error, rethrow (don't silently swallow real failures).
          // This rejects the surrounding Promise.all, which rejects fetchAllPages.
          throw error;
        })
    );
  }

  // All of pages 2..N are in flight concurrently by this point (the loop
  // above only *creates* promises, it never awaits between iterations).
  const pageResults = await Promise.all(pageFetches);

  // Concatenate strictly in page order, not settle order. Promise.all already
  // preserves input order regardless of which resolves first, but we sort by
  // the embedded page number too so correctness doesn't silently depend on
  // that guarantee alone.
  pageResults
    .filter((r): r is { page: number; results: T[] } => r !== null)
    .sort((a, b) => a.page - b.page)
    .forEach((r) => results.push(...r.results));

  return results;
}

/**
 * Backend hotel response with DecimalField strings.
 * DRF serializes DecimalField as string, e.g. "5.00", "902.00".
 */
interface HotelBackendResponse {
  id: string;
  name: string;
  /** RICH-issued hotel id (HTxxxxx); null for a hotel not from RICH. */
  mapping_id: string | null;
  station_iata: string;
  priority: string;
  allocator_priority: number | null;
  allocator_rules: AllocatorRule[];
  total_contracted_rooms: number;
  star_rating: number;
  distance_from_airport_km: string; // DecimalField → string
  drive_time_to_airport_minutes: number;
  near_airport: boolean;
  is_downtown: boolean;
  qe_catering_preferred: boolean;
  brand_group: string;
  security_status: string;
  security_approval_valid_until: string | null;
  areas_to_avoid_notes: string;
  contracted_rate_single: string; // DecimalField → string
  contracted_rate_double: string; // DecimalField → string
  currency: string;
  rate_card_peak_multiplier: string; // DecimalField → string
  late_checkout_fee: string; // DecimalField → string
  no_show_fee: string; // DecimalField → string
  cancellation_policy_hours: number;
  standard_checkin_local: string;
  standard_checkout_local: string;
  early_checkin_supported: boolean;
  late_checkout_supported: boolean;
  feedback_score_avg: string; // DecimalField → string
  rich_vendor_id: string | null;
  // Backend returns created_at/updated_at — drop them (not in Hotel type)
  created_at?: string;
  updated_at?: string;
  // compliance is optional — backend may not return it
  compliance?: Hotel['compliance'];
}

/**
 * Pure mapper: converts backend hotel response (with decimal strings)
 * to frontend Hotel type (with numbers).
 *
 * CRITICAL: DRF DecimalField serializes as string. All decimal fields must
 * be converted to numbers to avoid string-vs-number bugs (string concat in
 * sums, broken sorts/comparisons, type violations).
 *
 * Decimal fields (from task spec):
 * - distance_from_airport_km
 * - contracted_rate_single
 * - contracted_rate_double
 * - rate_card_peak_multiplier
 * - late_checkout_fee
 * - no_show_fee
 * - feedback_score_avg
 *
 * Integer fields stay as-is:
 * - drive_time_to_airport_minutes
 * - total_contracted_rooms
 * - cancellation_policy_hours
 * - star_rating
 */
export function mapHotelResponse(raw: HotelBackendResponse): Hotel {
  const {
    created_at,
    updated_at,
    distance_from_airport_km,
    contracted_rate_single,
    contracted_rate_double,
    rate_card_peak_multiplier,
    late_checkout_fee,
    no_show_fee,
    feedback_score_avg,
    ...rest
  } = raw;

  return {
    ...rest,
    priority: rest.priority as HotelPriority,
    brand_group: rest.brand_group as HotelBrandGroup,
    security_status: rest.security_status as SecurityStatus,
    star_rating: rest.star_rating as 1 | 2 | 3 | 4 | 5,
    distance_from_airport_km: parseFloat(distance_from_airport_km),
    contracted_rate_single: parseFloat(contracted_rate_single),
    contracted_rate_double: parseFloat(contracted_rate_double),
    rate_card_peak_multiplier: parseFloat(rate_card_peak_multiplier),
    late_checkout_fee: parseFloat(late_checkout_fee),
    no_show_fee: parseFloat(no_show_fee),
    feedback_score_avg: parseFloat(feedback_score_avg),
  };
}

/**
 * Fetch all stations from backend.
 * Fields exactly match frontend Station type — no mapping needed.
 */
export async function fetchStations(): Promise<Station[]> {
  // page_size passed as the helper's own param, not appended to the path —
  // fetchAllPages owns page_size exclusively (see its docstring). Note: the
  // backend's max_page_size for /stations/ is 100, so this is capped there
  // regardless of what we ask for.
  return fetchAllPages<Station>('/api/v1/stations/', 100);
}

/** All ranks, active and inactive (unpaginated, display_order). */
export async function fetchRanks(): Promise<Rank[]> {
  return get<Rank[]>('/api/v1/ranks/');
}

/** The system config row as the backend sends it (tenant is the FK id). */
type SystemConfigBackendResponse = Omit<SystemConfig, 'tenant_id' | 'notification_bands'> &
  TenantSettings & { tenant: string };

interface NotificationBandBackendResponse {
  id: NotificationBand['id'];
  batch_window_minutes: number;
  applies_from_days_before_checkin: number;
  applies_to_days_before_checkin: number;
}

/**
 * The deployment's system config, from the backend -- never a hardcoded
 * tenant constant. One /system-configs/ read gives both the full config
 * (store.systemConfig) and the settings subset (store.tenantSettings).
 * null when the user's tenant has no config row.
 */
export async function fetchSystemConfig(): Promise<{ settings: TenantSettings; config: SystemConfig } | null> {
  const page = await get<PaginatedResponse<SystemConfigBackendResponse>>('/api/v1/system-configs/');
  const raw = page.results[0];
  if (!raw) return null;
  const bands = await fetchAllPages<NotificationBandBackendResponse>('/api/v1/notification-bands/', 100);
  return {
    settings: toTenantSettings(raw),
    config: {
      tenant_id: raw.tenant,
      scc_single_occupancy: raw.scc_single_occupancy,
      na_routes_single: raw.na_routes_single,
      na_stations: raw.na_stations,
      domestic_after_na_single: raw.domestic_after_na_single,
      next_roster_fill_window_days: raw.next_roster_fill_window_days,
      short_stay_min_gap_hours: raw.short_stay_min_gap_hours,
      same_flight_same_hotel: raw.same_flight_same_hotel,
      time_shift_threshold_hours: raw.time_shift_threshold_hours,
      notification_bands: bands.map((b) => ({
        id: b.id,
        batch_window_minutes: b.batch_window_minutes,
        applies_from_days_before_checkin: b.applies_from_days_before_checkin,
        applies_to_days_before_checkin: b.applies_to_days_before_checkin,
      })),
      freeze_notifications_on_disruption: raw.freeze_notifications_on_disruption,
      inventory_increase_alert_threshold_percent: raw.inventory_increase_alert_threshold_percent,
      allow_crew_opt_out_hotel: raw.allow_crew_opt_out_hotel,
      allow_crew_opt_out_transport: raw.allow_crew_opt_out_transport,
      allow_home_base_hotel_assignment: raw.allow_home_base_hotel_assignment,
      use_rich_for_inventory: raw.use_rich_for_inventory,
      cae_feed_source: raw.cae_feed_source,
      pii_encryption_enabled: raw.pii_encryption_enabled,
      audit_chain_verify_schedule: raw.audit_chain_verify_schedule,
      vendor_mask_crew_contacts: raw.vendor_mask_crew_contacts,
      force_single_occupancy_all_ranks: raw.force_single_occupancy_all_ranks,
    },
  };
}

/** GET /api/v1/branding/ -- the deployment tenant (login required). */
export async function fetchBranding(): Promise<Branding> {
  return get<Branding>('/api/v1/branding/');
}

function toTenantSettings(config: TenantSettings): TenantSettings {
  return {
    id: config.id,
    allow_home_base_hotel_assignment: config.allow_home_base_hotel_assignment,
    home_base_layover_max_hours: config.home_base_layover_max_hours,
    booking_lead_days: config.booking_lead_days,
    home_country_code: config.home_country_code,
    book_domestic_operational: config.book_domestic_operational,
    book_international_operational: config.book_international_operational,
    book_domestic_non_operational: config.book_domestic_non_operational,
    book_international_non_operational: config.book_international_non_operational,
  };
}

/** The airline settings ops may change here (SUPER_ADMIN only; audit-logged by the backend). */
export type TenantSettingsChanges = Partial<
  Pick<
    TenantSettings,
    | 'allow_home_base_hotel_assignment'
    | 'home_base_layover_max_hours'
    | 'home_country_code'
    | BookingMatrixKey
  >
>;

export async function updateTenantSettings(id: number, changes: TenantSettingsChanges): Promise<TenantSettings> {
  return toTenantSettings(await patch<TenantSettings>(`/api/v1/system-configs/${id}/`, changes));
}

export type RankInput = Pick<Rank, 'code' | 'name' | 'display_order' | 'is_active'>;

/** SUPER_ADMIN only. */
export async function createRank(input: RankInput): Promise<Rank> {
  return post<Rank>('/api/v1/ranks/', input);
}

/** SUPER_ADMIN only. The code is immutable, so it is never sent. */
export async function updateRank(code: string, changes: Partial<Omit<RankInput, 'code'>>): Promise<Rank> {
  return patch<Rank>(`/api/v1/ranks/${encodeURIComponent(code)}/`, changes);
}

/**
 * Fetch all hotels from backend.
 * Applies decimal→number mapper to convert DRF DecimalField strings.
 */
export async function fetchHotels(): Promise<Hotel[]> {
  // page_size passed as the helper's own param — see fetchStations comment.
  // Backend max_page_size for /hotels/ is also 100.
  const rawHotels = await fetchAllPages<HotelBackendResponse>('/api/v1/hotels/', 100);
  return rawHotels.map(mapHotelResponse);
}

/**
 * Hotel page fetch params for server-side filtering + pagination.
 * Stage FW-4b + FW-4c.
 */
export interface HotelPageParams {
  page?: number;
  search?: string;
  station_iata?: string;
  priority?: string;
  security_status?: string;
  brand_group?: string;
  page_size?: number; // FW-4c: client-configurable page size
}

/**
 * Fetch a single page of hotels with server-side filtering + pagination.
 * Stage FW-4b + FW-4c.
 *
 * Returns the raw DRF envelope with count/next/previous/results.
 * Applies decimal→number mapper to results.
 */
export async function fetchHotelsPage(
  params: HotelPageParams = {}
): Promise<PaginatedResponse<Hotel>> {
  // Build query string, omitting empty/ANY/page=1 params
  const queryParams = new URLSearchParams();

  if (params.page && params.page > 1) {
    queryParams.append('page', params.page.toString());
  }
  if (params.search && params.search.trim()) {
    queryParams.append('search', params.search.trim());
  }
  if (params.station_iata && params.station_iata.trim()) {
    queryParams.append('station_iata', params.station_iata.trim());
  }
  if (params.priority && params.priority !== 'ANY') {
    queryParams.append('priority', params.priority);
  }
  if (params.security_status && params.security_status !== 'ANY') {
    queryParams.append('security_status', params.security_status);
  }
  if (params.brand_group && params.brand_group.trim()) {
    queryParams.append('brand_group', params.brand_group.trim());
  }
  // FW-4c: pass page_size explicitly when provided
  if (params.page_size !== undefined) {
    queryParams.append('page_size', params.page_size.toString());
  }

  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/hotels/?${queryString}` : '/api/v1/hotels/';

  const response = await get<PaginatedResponse<HotelBackendResponse>>(path);

  // Map results through decimal→number converter
  return {
    count: response.count,
    next: response.next,
    previous: response.previous,
    results: response.results.map(mapHotelResponse),
  };
}

/**
 * Station page fetch params for server-side filtering + pagination.
 * Stage FW-4c piece 3.
 */
export interface StationPageParams {
  page?: number;
  search?: string;
  region?: string;
  is_home_base?: 'ANY' | 'YES' | 'NO'; // Frontend filter values
  is_north_american?: boolean;
  country_code?: string;
  page_size?: number; // FW-4c: client-configurable page size
}

/**
 * Fetch a single page of stations with server-side filtering + pagination.
 * Stage FW-4c piece 3.
 *
 * Returns the raw DRF envelope with count/next/previous/results.
 * NO mapper needed — Station fields match backend exactly.
 */
export async function fetchStationsPage(
  params: StationPageParams = {}
): Promise<PaginatedResponse<Station>> {
  // Build query string, omitting empty/ANY/page=1 params
  const queryParams = new URLSearchParams();

  if (params.page && params.page > 1) {
    queryParams.append('page', params.page.toString());
  }
  if (params.search && params.search.trim()) {
    queryParams.append('search', params.search.trim());
  }
  if (params.region && params.region !== 'ANY') {
    queryParams.append('region', params.region);
  }
  // is_home_base: convert YES→'true', NO→'false', ANY→omit
  if (params.is_home_base && params.is_home_base !== 'ANY') {
    queryParams.append('is_home_base', params.is_home_base === 'YES' ? 'true' : 'false');
  }
  if (params.is_north_american !== undefined) {
    queryParams.append('is_north_american', params.is_north_american.toString());
  }
  if (params.country_code && params.country_code.trim()) {
    queryParams.append('country_code', params.country_code.trim());
  }
  // FW-4c: pass page_size explicitly when provided
  if (params.page_size !== undefined) {
    queryParams.append('page_size', params.page_size.toString());
  }

  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/stations/?${queryString}` : '/api/v1/stations/';

  const response = await get<PaginatedResponse<Station>>(path);

  // Return as-is — no mapper needed for stations
  return response;
}

// =============================================================================
// FW-6 piece 2b: Crew master list (paginated, non-PII)
// =============================================================================

/**
 * Crew page fetch params for server-side filtering + pagination.
 * Stage FW-6 piece 2b.
 */
export interface CrewPageParams {
  page?: number;
  search?: string;
  rank?: string;
  base_iata?: string;
  is_active?: boolean;
  page_size?: number;
}

/**
 * Fetch a single page of crew with server-side filtering + pagination.
 * Stage FW-6 piece 2b.
 *
 * Returns the raw DRF envelope with count/next/previous/results.
 * NO mapper needed — backend CrewListItem fields match frontend exactly (non-PII).
 *
 * Backend filters:
 * - rank (exact match)
 * - base_iata (exact match)
 * - is_active (boolean)
 * - search (full_name + staff_number)
 */
export async function fetchCrewPage(
  params: CrewPageParams = {}
): Promise<PaginatedResponse<CrewListItem>> {
  // Build query string, omitting empty/ANY/page=1 params
  const queryParams = new URLSearchParams();

  if (params.page && params.page > 1) {
    queryParams.append('page', params.page.toString());
  }
  if (params.search && params.search.trim()) {
    queryParams.append('search', params.search.trim());
  }
  if (params.rank && params.rank !== 'ANY') {
    queryParams.append('rank', params.rank);
  }
  if (params.base_iata && params.base_iata.trim()) {
    queryParams.append('base_iata', params.base_iata.trim());
  }
  // is_active: only send when explicitly set (true or false), omit undefined
  if (params.is_active !== undefined) {
    queryParams.append('is_active', params.is_active.toString());
  }
  // FW-6: pass page_size explicitly when provided
  if (params.page_size !== undefined) {
    queryParams.append('page_size', params.page_size.toString());
  }

  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/crew/?${queryString}` : '/api/v1/crew/';

  const response = await get<PaginatedResponse<CrewListItem>>(path);

  // Return as-is — no mapper needed (backend fields match CrewListItem exactly)
  return response;
}

// =============================================================================
// FW-5: Roster Publications + Lines
// =============================================================================

/**
 * Backend RosterPublication response shape.
 * Backend serializes tenant FK without _id suffix.
 * Frontend type expects tenant_id.
 */
interface RosterPublicationBackendResponse {
  id: string;
  tenant: string; // Backend FK field (no _id suffix)
  roster_month: string;
  source: 'CAE' | 'ARMS' | 'MANIFEST';
  cae_fetch_batch_id: string | null;
  published_at: string;
  status: RosterPublication['status'];
  total_lines: number;
  total_crew_impacted: number;
}

/**
 * Fetch roster publications from backend.
 * Maps tenant→tenant_id.
 *
 * Backend returns a simple array (not paginated) since there are few publications.
 * If backend returns paginated envelope, we handle both cases.
 */
export async function fetchRosterPublications(): Promise<RosterPublication[]> {
  const response = await get<RosterPublicationBackendResponse[] | PaginatedResponse<RosterPublicationBackendResponse>>(
    '/api/v1/roster-publications/'
  );

  // Handle both paginated and unpaginated responses
  const rawPubs = Array.isArray(response) ? response : response.results;

  return rawPubs.map(({ tenant, ...rest }) => ({
    ...rest,
    tenant_id: tenant,
    cae_fetch_batch_id: rest.cae_fetch_batch_id,
  }));
}

/**
 * Backend RosterLine response shape.
 * Backend serializes FKs without _id suffix: crew, publication, prev_version.
 * Station FKs (arrival_iata, etc.) are already IATA strings — no mapping needed.
 * Backend does NOT return some frontend fields (duty_code, duty_code_notes).
 */
interface RosterLineBackendResponse {
  id: string;
  publication: string; // FK without _id
  crew: string; // FK without _id
  crew_name: string;
  crew_staff_number: string;
  crew_rank: string;
  crew_base: string;
  line_type: RosterLineType;
  flight_number: string | null;
  origin_iata: string | null;
  destination_iata: string | null;
  std_local: string | null;
  sta_local: string | null;
  training_id: string | null;
  office_duty_id: string | null;
  surface_travel_id: string | null;
  oat_partner_airline_iata: string | null;
  oat_partner_flight_number: string | null;
  arrival_iata: string | null;
  arrival_local: string | null;
  departure_iata: string | null;
  departure_local: string | null;
  arrival_flight_number: string | null;
  arrival_flight_activity_code: string | null;
  arrival_flight_airline_code: string | null;
  arrival_flight_origin_iata: string | null;
  arrival_flight_arr_utc: string | null;
  arrival_flight_arr_local: string | null;
  departure_flight_number: string | null;
  departure_flight_activity_code: string | null;
  departure_flight_airline_code: string | null;
  departure_flight_destination_iata: string | null;
  departure_flight_dep_utc: string | null;
  departure_flight_dep_local: string | null;
  arrival_pattern_label: string | null;
  departure_pattern_label: string | null;
  pattern_labels: string[];
  operational_type: import('@/lib/types').OperationalType | null;
  operational_basis: string;
  stay_type: StayType | null;
  is_home_base: boolean;
  has_turnaround: boolean;
  incomplete_reason: string | null;
  layover_type: import('@/lib/types').LayoverType;
  home_base_override: boolean;
  home_base_override_reason: string;
  status: RosterLineStatus;
  roster_type: RosterType;
  prev_version: string | null; // FK without _id
  superseded_by: string | null; // FK without _id
  status_detail: RosterLineStatusDetail;
  modified_at: string | null;
  modified_reason: string | null;
  // Backend does NOT return: duty_code, duty_code_notes
}

/**
 * Maps backend RosterLine to frontend RosterLine.
 * - Renames crew→crew_id, publication→publication_id, prev_version→prev_version_id,
 *   superseded_by→superseded_by_id
 * - Sets missing frontend fields to null
 * - Station fields pass through (already IATA strings)
 */
function mapRosterLineResponse(raw: RosterLineBackendResponse): RosterLine {
  const { crew, publication, prev_version, superseded_by, ...rest } = raw;

  return {
    ...rest,
    crew_id: crew,
    publication_id: publication,
    prev_version_id: prev_version,
    superseded_by_id: superseded_by,
    roster_type: rest.roster_type,
    // Fields missing from backend serializer
    duty_code: null,
    duty_code_notes: undefined,
  };
}

/**
 * Set or clear the hotel override on a home-base roster line (OPS_CONTROLLER /
 * SUPER_ADMIN; reason required). 409 when an active booking blocks turning it
 * off. `outcome` says what happens to booking -- never a booking inside D-4.
 */
export async function setHomeBaseOverride(
  lineId: string,
  override: boolean,
  reason: string,
): Promise<{ line: RosterLine; outcome: HomeBaseOverrideOutcome }> {
  const { outcome, ...raw } = await patch<RosterLineBackendResponse & { outcome: HomeBaseOverrideOutcome }>(
    `/api/v1/roster-lines/${encodeURIComponent(lineId)}/home-base-override/`,
    { override, reason },
  );
  return { line: mapRosterLineResponse(raw), outcome };
}

/**
 * Roster line page fetch params for server-side filtering + pagination.
 * Stage FW-5.
 */
export interface RosterLinePageParams {
  page?: number;
  page_size?: number;
  crew?: string;
  search?: string;
  arrival_iata?: string;
  status?: RosterLineStatus;
  roster_type?: RosterType;
  line_type?: RosterLineType[];
  /** Stored RosterLine.operational_type, multi-value OR'd. */
  operational_type?: import('@/lib/types').OperationalType[];
  /** true: only home-base stays; omitted: all lines. */
  is_home_base?: boolean;
  rank?: CrewRank[];
  /** Crew CURRENT base (IATA), multi-value: ?crew_base=DEL&crew_base=BOM. */
  crew_base?: string[];
  /** Server-side sort, e.g. "arr" / "-duration" (see components/modules/roster/rosterSort.ts). */
  ordering?: string;
  source?: 'CAE' | 'ARMS' | 'MANIFEST';
  arrival_local_after?: string;
  arrival_local_before?: string;
  /** CAE patternLabel of any duty of the stay, exact match (GCS-620). */
  pattern_label?: string;
  /** Domestic / International / Unknown, multi-value OR'd (GCS-618). */
  layover_type?: import('@/lib/types').LayoverType[];
}

/** One option for the roster's crew-base filter: a base active crew are
 * based at (current base), with how many. */
export interface CrewBaseOption {
  code: string;
  crew_count: number;
}

/** Crew bases for the roster filter, most crew first. */
export async function fetchRosterCrewBases(): Promise<CrewBaseOption[]> {
  return get<CrewBaseOption[]>('/api/v1/roster-lines/crew-bases/');
}

/** A roster-lines page. count = rows matching every filter, status included;
 * current_count = the not-CANCELLED rows under the same filters, ?status= ignored. */
export interface RosterLinesPage extends PaginatedResponse<RosterLine> {
  current_count: number;
}

/**
 * Fetch a single page of roster lines with server-side filtering + pagination.
 * Stage FW-5.
 *
 * Returns the raw DRF envelope with count/next/previous/results.
 * Applies FK field mapper (crew→crew_id, publication→publication_id, prev_version→prev_version_id).
 */
export async function fetchRosterLinesPage(
  params: RosterLinePageParams = {}
): Promise<RosterLinesPage> {
  // Build query string, omitting empty/page=1 params
  const queryParams = new URLSearchParams();

  if (params.page && params.page > 1) {
    queryParams.append('page', params.page.toString());
  }
  if (params.page_size !== undefined) {
    queryParams.append('page_size', params.page_size.toString());
  }
  if (params.crew && params.crew.trim()) {
    queryParams.append('crew', params.crew.trim());
  }
  if (params.search && params.search.trim()) {
    queryParams.append('search', params.search.trim());
  }
  if (params.arrival_iata && params.arrival_iata.trim()) {
    queryParams.append('arrival_iata', params.arrival_iata.trim());
  }
  if (params.status) {
    queryParams.append('status', params.status);
  }
  if (params.roster_type) {
    queryParams.append('roster_type', params.roster_type);
  }
  // line_type is an ARRAY → append each value as a repeated param
  if (params.line_type && params.line_type.length > 0) {
    params.line_type.forEach((type) => {
      queryParams.append('line_type', type);
    });
  }
  if (params.is_home_base !== undefined) {
    queryParams.append('is_home_base', String(params.is_home_base));
  }
  (params.operational_type ?? []).forEach((t) => queryParams.append('operational_type', t));
  (params.layover_type ?? []).forEach((t) => queryParams.append('layover_type', t));
  // rank is an ARRAY → append each value as a repeated param
  if (params.rank && params.rank.length > 0) {
    params.rank.forEach((r) => {
      queryParams.append('rank', r);
    });
  }
  // crew_base is an ARRAY → append each value as a repeated param
  if (params.crew_base && params.crew_base.length > 0) {
    params.crew_base.forEach((b) => {
      queryParams.append('crew_base', b);
    });
  }
  if (params.source) {
    queryParams.append('source', params.source);
  }
  if (params.ordering) {
    queryParams.append('ordering', params.ordering);
  }
  if (params.arrival_local_after) {
    queryParams.append('arrival_local_after', params.arrival_local_after);
  }
  if (params.arrival_local_before) {
    queryParams.append('arrival_local_before', params.arrival_local_before);
  }
  if (params.pattern_label && params.pattern_label.trim()) {
    queryParams.append('pattern_label', params.pattern_label.trim());
  }

  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/roster-lines/?${queryString}` : '/api/v1/roster-lines/';

  const response = await get<PaginatedResponse<RosterLineBackendResponse> & { current_count: number }>(path);

  // Map results through FK field renamer
  return {
    count: response.count,
    current_count: response.current_count,
    next: response.next,
    previous: response.previous,
    results: response.results.map(mapRosterLineResponse),
  };
}

/** One roster line by id (any status -- e.g. a MODIFIED line's prev_version,
 * which is CANCELLED), mapped like a list row. */
export async function fetchRosterLine(id: string): Promise<RosterLine> {
  const raw = await get<RosterLineBackendResponse>(`/api/v1/roster-lines/${encodeURIComponent(id)}/`);
  return mapRosterLineResponse(raw);
}

// =============================================================================
// Bulk Roster Line Upload
// =============================================================================

export interface BulkRosterLineItem {
  staff_number: string;
  line_type: RosterLineType;
  flight_number?: string;
  departure_iata?: string;
  arrival_iata?: string;
  std_local?: string;
  sta_local?: string;
}

export interface BulkRosterLineInput {
  roster_type: RosterType;
  items: BulkRosterLineItem[];
}

export interface BulkRosterLineResponse {
  message: string;
  created_count: number;
}

export interface BulkRosterLineError {
  errors: Array<{
    row: number;
    errors: Record<string, string>;
  }>;
  message: string;
}

/**
 * Bulk create roster lines from CSV/Excel upload.
 * POST /api/v1/bulk-roster-lines/
 *
 * @param input - Roster type and array of roster line items
 * @returns Success response with created count
 */
export async function postBulkRosterLines(input: BulkRosterLineInput): Promise<BulkRosterLineResponse> {
  return post<BulkRosterLineResponse>('/api/v1/bulk-roster-lines/', input);
}

// =============================================================================
// FW-6 piece 2c: Crew name lookup via id__in
// =============================================================================

/**
 * Fetch crew by multiple IDs in a single request.
 * Stage FW-6 piece 2c.
 *
 * Uses id__in query param: GET /api/v1/crew/?id__in=uuid1,uuid2,...&page_size=N
 * Backend supports this for efficient crew name lookups on roster pages.
 *
 * @param ids - Array of crew UUIDs (e.g., ['crw_0042', 'crw_0100'])
 * @returns Array of CrewListItem (non-PII crew data: id, staff_number, full_name, rank, base_iata, is_active)
 *
 * CRITICAL:
 * - Returns empty array if ids is empty WITHOUT calling the API (guard)
 * - Deduplicates ids before making the request
 * - Sets page_size to ids.length to ensure all results fit on one page
 * - NO mapper needed — backend fields match CrewListItem exactly
 */
export async function fetchCrewByIds(ids: string[]): Promise<CrewListItem[]> {
  // Guard: return empty array if no ids provided
  if (ids.length === 0) {
    return [];
  }

  // Deduplicate ids
  const uniqueIds = [...new Set(ids)];

  // Batch the lookup. A single id__in=... with thousands of UUIDs builds a URL
  // of ~37 chars per id; 2786 ids => ~100k-char URL, which the browser/server
  // rejects with "TypeError: Failed to fetch". Splitting into fixed-size
  // batches keeps every URL well under the ~8k-char limit (100 ids ≈ 3.7k chars).
  const BATCH_SIZE = 100;

  const batches: string[][] = [];
  for (let i = 0; i < uniqueIds.length; i += BATCH_SIZE) {
    batches.push(uniqueIds.slice(i, i + BATCH_SIZE));
  }

  // Fetch all batches in parallel. Each batch is one small id__in request.
  const responses = await Promise.all(
    batches.map((batch) => {
      const queryParams = new URLSearchParams();
      queryParams.append('id__in', batch.join(','));
      queryParams.append('page_size', batch.length.toString());
      const path = `/api/v1/crew/?${queryParams.toString()}`;
      return get<PaginatedResponse<CrewListItem>>(path);
    })
  );

  // Merge results from every batch into one flat array.
  return responses.flatMap((response) => response.results);
}

// =============================================================================
// FW-7 projection piece A: Projection Runs + Entries (backend wiring)
// =============================================================================

/**
 * Backend ProjectionRun response shape.
 * Backend serializes FK fields without _id suffix.
 * Frontend type expects publication_id, generated_by_user_id.
 */
interface ProjectionRunBackendResponse {
  id: string;
  publication: string; // Backend FK field (no _id suffix)
  roster_month: string;
  generated_at: string;
  generated_by_user: string; // Backend FK field (no _id suffix)
  status: import('@/lib/types').ProjectionRunStatus;
  capacity_fetched_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Maps backend ProjectionRun to frontend ProjectionRun.
 * - Renames publication→publication_id, generated_by_user→generated_by_user_id
 * - Drops created_at/updated_at (not in frontend type)
 */
function mapProjectionRunResponse(raw: ProjectionRunBackendResponse): import('@/lib/types').ProjectionRun {
  const { publication, generated_by_user, created_at, updated_at, ...rest } = raw;

  return {
    ...rest,
    publication_id: publication,
    generated_by_user_id: generated_by_user,
  };
}

/**
 * Fetch projection runs from backend.
 * Maps publication→publication_id, generated_by_user→generated_by_user_id.
 * Stage FW-7 projection piece A (bugfix: use fetchAllPages for future-proofing).
 *
 * CRITICAL:
 * - Uses fetchAllPages to follow DRF `next` links (future-proofing: if runs grow beyond 50)
 * - Currently 1 run, but same 50-cap bug would bite if runs accumulate
 */
export async function fetchProjectionRuns(): Promise<import('@/lib/types').ProjectionRun[]> {
  // Fetch ALL pages (future-proof: if runs grow beyond 50)
  const rawRuns = await fetchAllPages<ProjectionRunBackendResponse>('/api/v1/projection-runs/');

  // Map results through FK field renamer
  return rawRuns.map(mapProjectionRunResponse);
}

/**
 * Backend ProjectionEntry response shape.
 * Backend serializes FK fields without _id suffix: run, hotel, hotel_response_user.
 * Backend status "PROJECTED" → frontend "DRAFT".
 */
interface ProjectionEntryBackendResponse {
  id: string;
  run: string; // FK without _id
  hotel: string; // FK without _id
  station_iata: string;
  date_local: string;
  total_rooms_required: number;
  single_rooms: number;
  double_rooms: number;
  double_single_use_rooms: number;
  crew_count: number;
  fd_crew: number;
  cc_crew: number;
  capacity_available: number | null;
  capacity_left: number | null;
  cohort_breakdown: import('@/lib/types').CohortBreakdownItem[];
  rooms_carried_over_from_previous_month: number;
  status: string; // Backend may return "PROJECTED"
  hotel_response_rooms_offered: number | null;
  hotel_response_at: string | null;
  hotel_response_user: string | null; // FK without _id
  hotel_response_notes: string;
  cascade_level: import('@/lib/types').CascadeLevel;
  manual_intervention_required: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Maps backend ProjectionEntry to frontend ProjectionEntry.
 * - Renames run→run_id, hotel→hotel_id, hotel_response_user→hotel_response_user_id
 * - Maps status "PROJECTED"→"DRAFT" (defensive: if already valid frontend status, pass through)
 * - Drops created_at/updated_at (not in frontend type)
 */
function mapProjectionEntryResponse(raw: ProjectionEntryBackendResponse): import('@/lib/types').ProjectionEntry {
  const { run, hotel, hotel_response_user, status, created_at, updated_at, ...rest } = raw;

  // Map status: "PROJECTED"→"DRAFT", else pass through (defensive)
  const mappedStatus: import('@/lib/types').ProjectionEntryStatus =
    status === 'PROJECTED' ? 'DRAFT' : (status as import('@/lib/types').ProjectionEntryStatus);

  return {
    ...rest,
    run_id: run,
    hotel_id: hotel,
    hotel_response_user_id: hotel_response_user,
    status: mappedStatus,
  };
}

/**
 * Fetch projection entries from backend.
 * Maps run→run_id, hotel→hotel_id, hotel_response_user→hotel_response_user_id.
 * Maps status "PROJECTED"→"DRAFT".
 * Stage FW-7 projection piece A (bugfix: use fetchAllPages to follow pagination).
 *
 * @param runId - Run UUID to filter entries server-side via ?run= query param
 * @returns Array of ProjectionEntry (mapped to frontend shape)
 *
 * CRITICAL:
 * - Uses backend ?run= filter to fetch only the target run's entries
 * - Uses fetchAllPages to follow DRF `next` links (pagination)
 * - Maps run→run_id, hotel→hotel_id, hotel_response_user→hotel_response_user_id
 * - Maps status "PROJECTED"→"DRAFT"
 */
/** Projected vs actual rooms per (hotel, station, night) for a run and date range (YYYY-MM-DD, max 62 days). */
export async function fetchProjectedVsActual(
  runId: string,
  dateFrom: string,
  dateTo: string,
): Promise<import('@/lib/types').ProjectedVsActualCell[]> {
  const query = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
  const body = await get<{ cells: import('@/lib/types').ProjectedVsActualCell[] }>(
    `/api/v1/projection-runs/${encodeURIComponent(runId)}/projected-vs-actual/?${query.toString()}`,
  );
  return body.cells;
}

export type CascadePreviewAction = 'reject' | 'modify';

export interface CascadePlacement {
  hotel_id: string;
  hotel_name: string;
  priority: string;
  rooms: number;
  /** The hotel's contracted rooms: what the cascade sizes against today. */
  capacity: number;
  flights: string[];
}

/** GET /projection-entries/{id}/cascade-preview/: what a reject / modify would move, nothing written. */
export interface CascadePreview {
  action: CascadePreviewAction;
  /** What really moves: whole cohorts, so it can exceed total − rooms offered. */
  rooms_to_cascade: number;
  /** Modify: the whole cohorts staying at this hotel (≤ rooms offered); reject: none. */
  kept: { rooms: number; flights: string[] };
  placements: CascadePlacement[];
  unplaced: { rooms: number; flights: string[] };
  tiers_tried: string[];
}

export async function previewCascade(
  entryId: string, action: CascadePreviewAction, roomsOffered?: number,
): Promise<CascadePreview> {
  const rooms = action === 'modify' && roomsOffered !== undefined ? `&rooms_offered=${roomsOffered}` : '';
  return get<CascadePreview>(`/api/v1/projection-entries/${encodeURIComponent(entryId)}/cascade-preview/?action=${action}${rooms}`);
}

/**
 * GCS-684: the run's projection entries as an Excel (ops template layout) or
 * CSV file (backend streams it; scoped like the list). station: only that
 * station's entries.
 */
export type ProjectionExportFormat = 'xlsx' | 'csv';

export async function exportProjectionEntries(
  runId: string, format: ProjectionExportFormat, station?: string,
): Promise<Blob> {
  const stationParam = station ? `&station=${encodeURIComponent(station)}` : '';
  return getBlob(`/api/v1/projection-entries/export/?run=${encodeURIComponent(runId)}&format=${format}${stationParam}`);
}

export async function fetchProjectionEntries(runId: string): Promise<import('@/lib/types').ProjectionEntry[]> {
  // Fetch ALL pages with backend ?run= filter
  const rawEntries = await fetchAllPages<ProjectionEntryBackendResponse>(`/api/v1/projection-entries/?run=${runId}`);

  // Map results through FK field renamer + status mapper
  return rawEntries.map(mapProjectionEntryResponse);
}

// =============================================================================
// FW-7 projection piece B: Inventory Alerts (backend wiring)
// =============================================================================

/**
 * Backend InventoryAlert response shape.
 * Backend serializes FK fields without _id suffix: run, hotel, acknowledged_by_user.
 * Backend serializes DecimalField as string: excess_percent.
 */
interface InventoryAlertBackendResponse {
  id: string;
  run: string; // FK without _id
  hotel: string; // FK without _id
  station_iata: string;
  date_local: string;
  type: InventoryAlertType;
  contracted_rooms: number;
  demand_rooms: number;
  excess_rooms: number;
  excess_percent: string; // DecimalField → string (e.g., "96.00")
  status: InventoryAlertStatus;
  acknowledged_by_user: string | null; // FK without _id
  acknowledged_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
}

/**
 * Maps backend InventoryAlert to frontend InventoryAlert.
 * - Renames run→run_id, hotel→hotel_id, acknowledged_by_user→acknowledged_by_user_id
 * - Converts excess_percent from string to number (DRF DecimalField)
 * - Passes through type/status (no remapping needed)
 * - Drops created_at/updated_at (not in frontend type)
 */
function mapInventoryAlertResponse(raw: InventoryAlertBackendResponse): InventoryAlert {
  const { run, hotel, acknowledged_by_user, excess_percent, created_at, updated_at, ...rest } = raw;

  return {
    ...rest,
    run_id: run,
    hotel_id: hotel,
    acknowledged_by_user_id: acknowledged_by_user,
    excess_percent: parseFloat(excess_percent), // "96.00" → 96.0
  };
}

/**
 * Fetch inventory alerts from backend.
 * Maps run→run_id, hotel→hotel_id, acknowledged_by_user→acknowledged_by_user_id.
 * Converts excess_percent string to number.
 * Stage FW-7 projection piece B (frontend wiring).
 *
 * @param runId - Optional run UUID to filter alerts server-side via ?run= query param. If omitted, fetches all alerts.
 * @returns Array of InventoryAlert (mapped to frontend shape)
 *
 * CRITICAL:
 * - If runId provided: uses backend ?run= filter to fetch only that run's alerts
 * - If runId omitted: fetches ALL alerts across all runs (for inventory-alerts page)
 * - Uses fetchAllPages to follow DRF `next` links (pagination)
 * - Maps run→run_id, hotel→hotel_id, acknowledged_by_user→acknowledged_by_user_id
 * - Converts excess_percent from string to number (DRF DecimalField)
 */
export async function fetchInventoryAlerts(runId?: string): Promise<InventoryAlert[]> {
  // Fetch ALL pages with optional backend ?run= filter. page_size is NOT
  // appended here — fetchAllPages owns page_size exclusively and applies its
  // own default (1000) so the path string can't collide with it.
  const queryParams = new URLSearchParams();
  if (runId) {
    queryParams.append('run', runId);
  }

  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/inventory-alerts/?${queryString}` : '/api/v1/inventory-alerts/';
  const rawAlerts = await fetchAllPages<InventoryAlertBackendResponse>(path);

  // Map results through FK field renamer + decimal converter
  return rawAlerts.map(mapInventoryAlertResponse);
}

// =============================================================================
// FW-7 projection piece C: Unallotted Reports (backend wiring)
// =============================================================================

/**
 * Backend UnallottedReport response shape.
 * Backend serializes FK fields without _id suffix: run, affected_crew, resolved_hotel, resolved_by_user.
 */
interface UnallottedReportBackendResponse {
  id: string;
  run: string; // FK without _id
  station_iata: string;
  date_local: string;
  rooms_short: number;
  affected_crew: string[]; // Array of crew UUIDs
  affected_flight_numbers: string[];
  cascade_path_tried: string[];
  reason: string | null;
  resolved_at: string | null;
  resolved_hotel: string | null; // FK without _id
  resolved_hotel_mapping_id: string | null;
  resolved_by_user: string | null; // FK without _id
  resolution_notes: string;
  created_at: string;
  updated_at: string;
}

/**
 * Maps backend UnallottedReport to frontend UnallottedReport.
 * - Renames run→run_id, affected_crew→affected_crew_ids, resolved_hotel→resolved_hotel_id, resolved_by_user→resolved_by_user_id
 * - Drops created_at/updated_at (not in frontend type)
 */
function mapUnallottedReportResponse(raw: UnallottedReportBackendResponse): UnallottedReport {
  const { run, affected_crew, resolved_hotel, resolved_by_user, created_at, updated_at, ...rest } = raw;

  return {
    ...rest,
    run_id: run,
    affected_crew_ids: affected_crew,
    resolved_hotel_id: resolved_hotel,
    resolved_by_user_id: resolved_by_user,
  };
}

/**
 * Fetch unallotted reports from backend.
 * Maps run→run_id, affected_crew→affected_crew_ids.
 * Stage FW-7 projection piece C (frontend wiring).
 *
 * @param runId - Run UUID to filter reports server-side via ?run= query param
 * @returns Array of UnallottedReport (mapped to frontend shape)
 *
 * CRITICAL:
 * - Uses backend ?run= filter to fetch only the target run's reports
 * - Uses fetchAllPages to follow DRF `next` links (pagination)
 * - Maps run→run_id, affected_crew→affected_crew_ids
 */
export async function fetchUnallottedReports(runId: string): Promise<UnallottedReport[]> {
  // Fetch ALL pages with backend ?run= filter
  const rawReports = await fetchAllPages<UnallottedReportBackendResponse>(`/api/v1/unallotted-reports/?run=${runId}`);

  // Map results through FK field renamer
  return rawReports.map(mapUnallottedReportResponse);
}

// =============================================================================
// FW-8 booking piece 1: Bookings table + filters (backend wiring)
// =============================================================================

/**
 * Backend Booking response with FK fields without _id suffix and cost fields as strings.
 * DRF serializes DecimalField as string (e.g., "902.00").
 * Backend FK fields: publication, hotel, primary_crew, companion_crew, superseded_by_booking, supersedes_booking, hotel_response_user.
 * Backend created_by_user is an INTEGER (e.g., 1), not a UUID string.
 * Station field (station_iata) is already an IATA string — no mapping needed.
 */
export interface BookingBackendResponse {
  id: string;
  publication: string; // FK without _id
  hotel: string; // FK without _id
  station_iata: string; // IATA string (not FK)
  check_in_utc: string;
  check_out_utc: string;
  num_nights: number;
  room_type: import('@/lib/types').RoomType;
  occupancy: import('@/lib/types').Occupancy;
  primary_crew: string; // FK without _id
  companion_crew: string | null; // FK without _id
  arrival_flight_number: string;
  departure_flight_number: string | null;
  crew_opted_out_hotel: boolean;
  crew_opted_out_transport: boolean;
  crew_opt_out_reason: string;
  crew_opt_out_at: string | null;
  status: import('@/lib/types').BookingStatus;
  // Cost fields: DRF DecimalField → string
  contracted_rate: string;
  taxes: string;
  late_checkout_fee: string;
  no_show_fee: string;
  cancellation_fee: string;
  transport_charges: string;
  waiting_time_charges: string;
  operational_penalties: string;
  management_fee: string;
  technology_platform_cost: string;
  bidder_markup: string;
  total_tcoat: string;
  currency: string; // String, not DecimalField
  is_qatar_executive: boolean;
  is_amiri_flight: boolean;
  is_oal: boolean;
  source_kind: import('@/lib/types').BookingSourceKind;
  is_home_base_override: boolean;
  home_base_override_reason: string;
  operational_type: import('@/lib/types').OperationalType | null; // null: no roster line
  operational_basis: string;
  crew_group: import('@/lib/types').BookingCrewGroup; // GCS-700, from the primary crew's rank
  arrival_flight: import('@/lib/types').BookingFlight | null; // null: no roster line / leg
  departure_flight: import('@/lib/types').BookingFlight | null;
  superseded_by_booking: string | null; // FK without _id
  supersedes_booking: string | null; // FK without _id
  created_at: string;
  created_by_user: number; // INTEGER (not string UUID)
  hotel_response_at: string | null;
  hotel_response_user: string | null; // FK without _id
  // OTP fields
  checkin_otp_sent: boolean;
  checkin_otp_regen_count: number;
  checkin_confirmed_at: string | null;
  checkin_confirmed_by_id: string | null;
  checkout_otp_sent: boolean;
  checkout_otp_regen_count: number;
  checkout_confirmed_at: string | null;
  checkout_confirmed_by_id: string | null;
  // Accrual fields (annotated — nullable)
  accrual_stage: import('@/lib/types').AccrualStage | null;
  invoice_approval_status: string | null;
  invoice_rejection_reason: string | null;
  invoice_approved_by: string | null;
  invoice_approved_at: string | null;
  invoice_submitted_at: string | null;
  accrual_checkout_date: string | null; // YYYY-MM-DD station-local
  accrual_period: string | null; // YYYY-MM
  invoiced_amount: string | null; // DecimalField → string
  invoice_ref: string | null;
  invoiced_at: string | null;
  paid_amount: string | null; // DecimalField → string
  paid_at: string | null;
  journal_ref: string | null;
  finance_notes: string | null;
  expected_amount: string | null; // annotated DecimalField → string
  variance_amount: string | null; // annotated DecimalField → string
  aging_days: number | null; // annotated integer
  // Backend may return updated_at — drop it (not in frontend type)
  updated_at?: string;
}

/**
 * Pure mapper: converts backend booking response to frontend Booking type.
 *
 * CRITICAL transformations:
 * - FK field renaming (publication→publication_id, hotel→hotel_id, primary_crew→primary_crew_id,
 *   companion_crew→companion_crew_id, superseded_by_booking→superseded_by_booking_id,
 *   supersedes_booking→supersedes_booking_id, hotel_response_user→hotel_response_user_id)
 * - created_by_user (number) → created_by_user_id (string via String())
 * - 13 cost fields from DecimalField strings to numbers (parseFloat)
 * - 4 accrual decimal fields from strings to numbers with null guards (invoiced_amount, paid_amount, expected_amount, variance_amount)
 * - Other accrual fields (accrual_stage, accrual_checkout_date, accrual_period, invoice_ref, invoiced_at, paid_at, journal_ref, finance_notes, aging_days) pass through unchanged
 * - station_iata/currency/status/room_type/occupancy/flags/dates pass through unchanged
 * - created_at is in frontend Booking type — keep it
 * - Drop updated_at (not in frontend type)
 */
export function mapBookingResponse(raw: BookingBackendResponse): import('@/lib/types').Booking {
  const {
    publication,
    hotel,
    primary_crew,
    companion_crew,
    superseded_by_booking,
    supersedes_booking,
    created_by_user,
    hotel_response_user,
    updated_at,
    // Cost fields (strings)
    contracted_rate,
    taxes,
    late_checkout_fee,
    no_show_fee,
    cancellation_fee,
    transport_charges,
    waiting_time_charges,
    operational_penalties,
    management_fee,
    technology_platform_cost,
    bidder_markup,
    total_tcoat,
    // Accrual decimal fields (strings → numbers with null guards)
    invoiced_amount,
    paid_amount,
    expected_amount,
    variance_amount,
    ...rest
  } = raw;

  return {
    ...rest,
    // Rename FK fields
    publication_id: publication,
    hotel_id: hotel,
    primary_crew_id: primary_crew,
    companion_crew_id: companion_crew,
    superseded_by_booking_id: superseded_by_booking,
    supersedes_booking_id: supersedes_booking,
    hotel_response_user_id: hotel_response_user,
    // created_by_user (number) → created_by_user_id (string)
    created_by_user_id: String(created_by_user),
    // Convert cost fields from strings to numbers
    contracted_rate: parseFloat(contracted_rate),
    taxes: parseFloat(taxes),
    late_checkout_fee: parseFloat(late_checkout_fee),
    no_show_fee: parseFloat(no_show_fee),
    cancellation_fee: parseFloat(cancellation_fee),
    transport_charges: parseFloat(transport_charges),
    waiting_time_charges: parseFloat(waiting_time_charges),
    operational_penalties: parseFloat(operational_penalties),
    management_fee: parseFloat(management_fee),
    technology_platform_cost: parseFloat(technology_platform_cost),
    bidder_markup: parseFloat(bidder_markup),
    total_tcoat: parseFloat(total_tcoat),
    // Convert accrual decimal fields from strings to numbers (null-safe)
    invoiced_amount: invoiced_amount !== null ? parseFloat(invoiced_amount) : null,
    paid_amount: paid_amount !== null ? parseFloat(paid_amount) : null,
    expected_amount: expected_amount !== null ? parseFloat(expected_amount) : null,
    variance_amount: variance_amount !== null ? parseFloat(variance_amount) : null,
    // updated_at: pass through or null if not present
    updated_at: updated_at ?? null,
    // Other accrual fields (accrual_stage, accrual_checkout_date, accrual_period, invoice_ref, invoiced_at, paid_at, journal_ref, finance_notes, aging_days) pass through from ...rest
    // station_iata, currency, status, room_type, occupancy, flags, dates, created_at pass through
  };
}

/**
 * Adapter: transforms backend Booking (with accrual fields) to frontend AccrualEntry.
 * Stage: Accruals wiring.
 *
 * AccrualEntry is the shape expected by app/accruals/page.tsx (Phase A interface).
 * Booking is the shape returned by the backend (accruals as fields on Booking, not separate entity).
 *
 * Field mapping:
 * - id: booking.id (accrual entry ID = booking ID)
 * - booking_id: booking.id
 * - hotel_id: booking.hotel_id
 * - station_iata: booking.station_iata
 * - checkout_date: booking.accrual_checkout_date (YYYY-MM-DD station-local)
 * - expected_amount: booking.expected_amount ?? 0 (annotated field, may be null)
 * - invoiced_amount: booking.invoiced_amount (null until invoiced)
 * - paid_amount: booking.paid_amount (null until paid)
 * - currency: booking.currency
 * - stage: booking.accrual_stage ?? 'CONSUMED' (default if null)
 * - invoice_id: null (not tracked on Booking — Phase A concept only)
 * - invoice_line_id: null (not tracked on Booking — Phase A concept only)
 * - invoiced_at: booking.invoiced_at (ISO datetime, null until invoiced)
 * - paid_at: booking.paid_at (ISO datetime, null until paid)
 * - aging_days: booking.aging_days ?? 0 (annotated field, may be null)
 * - accounting_period: booking.accrual_period ?? '' (YYYY-MM)
 * - journal_ref: booking.journal_ref ?? '' (e.g. ACCR/JUN/096)
 * - finance_notes: booking.finance_notes ?? ''
 * - invoice_ref: booking.invoice_ref ?? '' (hotel's invoice number)
 * - created_at: booking.created_at
 * - updated_at: '' (Booking doesn't have updated_at in frontend type)
 *
 * CRITICAL null handling:
 * - accrual_checkout_date: pass null through (not coerced to empty string)
 * - accrual_period, accrual_stage may be null → use sensible defaults
 * - expected_amount, aging_days may be null (annotated fields not always present) → pass null through
 * - invoiced_amount, paid_amount, invoiced_at, paid_at are null until the respective stage
 */
function mapBookingToAccrualEntry(booking: import('@/lib/types').Booking): import('@/lib/types').AccrualEntry {
  return {
    id: booking.id,
    booking_id: booking.id,
    hotel_id: booking.hotel_id,
    station_iata: booking.station_iata,
    checkout_date: booking.accrual_checkout_date,
    expected_amount: booking.expected_amount, // Pass null through (not 0)
    invoiced_amount: booking.invoiced_amount,
    paid_amount: booking.paid_amount,
    currency: booking.currency,
    stage: booking.accrual_stage ?? 'CONSUMED',
    invoice_id: null, // Not tracked on Booking (Phase A concept)
    invoice_line_id: null, // Not tracked on Booking (Phase A concept)
    invoiced_at: booking.invoiced_at,
    paid_at: booking.paid_at,
    aging_days: booking.aging_days, // Pass null through (not 0)
    accounting_period: booking.accrual_period ?? '',
    journal_ref: booking.journal_ref ?? '',
    finance_notes: booking.finance_notes ?? '',
    invoice_ref: booking.invoice_ref ?? '',
    created_at: booking.created_at,
    updated_at: booking.updated_at, // Pass through from backend (string | null)
  };
}

/**
 * Fetch all bookings from backend.
 * Stage FW-8 booking piece 1 (frontend, read-only).
 *
 * Uses fetchAllPages to follow DRF pagination (backend page_size=1000, 4,083 bookings = 5 pages).
 * Applies mapBookingResponse to convert FK fields and cost strings to numbers.
 *
 * @returns Array of Booking (mapped to frontend shape)
 *
 * CRITICAL:
 * - Fetches ALL bookings across all pages. Aggregate endpoints (dashboard, analytics, reports) page the
 *   entire table. The real fix is backend aggregate endpoints rather than increasing page_size.
 * - Uses fetchAllPages<BookingBackendResponse>('/api/v1/bookings/') — page_size is
 *   owned by fetchAllPages (default 1000), not appended to the path
 * - Maps each result through mapBookingResponse
 * - Does NOT touch s.bookings (10 consumers still use mock — projection, dashboard, etc.)
 */
export async function fetchBookings(): Promise<import('@/lib/types').Booking[]> {
  // Fetch ALL pages (backend returns 1000/page, 4,083 bookings total = 5 pages).
  // page_size is NOT appended here — fetchAllPages owns it exclusively and
  // applies its own default (1000).
  const rawBookings = await fetchAllPages<BookingBackendResponse>('/api/v1/bookings/');

  // Map results through FK field renamer + cost string→number converter
  return rawBookings.map(mapBookingResponse);
}

/**
 * Booking page fetch params for server-side filtering + pagination.
 * Used by the Booking page to avoid loading all bookings at once.
 */
export interface BookingPageParams {
  page?: number;
  page_size?: number;
  date_local?: string; // YYYY-MM-DD
  date_local__gte?: string; // YYYY-MM-DD
  date_local__lte?: string; // YYYY-MM-DD
  arrival_flight_number?: string;
  station_iata?: string;
  search?: string;
  status?: string;
  /** Hotel id (?hotel=<uuid>, BookingFilterSet "hotel" exact). */
  hotel?: string;
  /** Crew rank, multi-value, matching EITHER crew of the booking. */
  rank?: string[];
  /** Crew CURRENT base (IATA), multi-value, matching EITHER crew. */
  crew_base?: string[];
  /** GCS-700: the primary crew's group (backend rank map). */
  crew_group?: Exclude<import('@/lib/types').BookingCrewGroup, 'UNKNOWN'>;
  occupancy?: import('@/lib/types').Occupancy[];
  /** Stored Booking.operational_type, multi-value OR'd. undefined / empty = no filter. */
  operational_type?: import('@/lib/types').OperationalType[];
}

/**
 * Fetch a single page of bookings with server-side filtering + pagination.
 * Returns the raw DRF envelope with count/next/previous/results.
 * Applies mapBookingResponse to convert FK fields and cost strings to numbers.
 *
 * Backend supports filters:
 * - date_local (YYYY-MM-DD, also date_local__gte/date_local__lte)
 * - arrival_flight_number (also __icontains)
 * - station_iata (IATA code)
 * - search (matches crew full_name / staff_number / flight number)
 * - status
 * - status__in
 * - rank / crew_base (either crew), occupancy -- multi-value
 * - operational_type (OPERATIONAL / NON_OPERATIONAL) -- multi-value
 *
 * Response includes check_in_local and check_out_local (ISO strings with offset).
 */
/** One option for the booking screen's crew-base filter: a CURRENT base of
 * either crew on bookings the user can see, with how many bookings. */
export interface BookingCrewBaseOption {
  code: string;
  booking_count: number;
}

export async function fetchBookingCrewBases(): Promise<BookingCrewBaseOption[]> {
  return get<BookingCrewBaseOption[]>('/api/v1/bookings/crew-bases/');
}

export async function fetchBookingsPage(
  params: BookingPageParams = {}
): Promise<PaginatedResponse<import('@/lib/types').Booking>> {
  // Build query string, omitting empty params
  const queryParams = new URLSearchParams();

  if (params.page && params.page > 1) {
    queryParams.append('page', params.page.toString());
  }
  if (params.page_size !== undefined) {
    queryParams.append('page_size', params.page_size.toString());
  }
  if (params.date_local && params.date_local.trim()) {
    queryParams.append('date_local', params.date_local.trim());
  }
  if (params.date_local__gte && params.date_local__gte.trim()) {
    queryParams.append('date_local__gte', params.date_local__gte.trim());
  }
  if (params.date_local__lte && params.date_local__lte.trim()) {
    queryParams.append('date_local__lte', params.date_local__lte.trim());
  }
  if (params.arrival_flight_number && params.arrival_flight_number.trim()) {
    queryParams.append('arrival_flight_number', params.arrival_flight_number.trim());
  }
  if (params.station_iata && params.station_iata.trim()) {
    queryParams.append('station_iata', params.station_iata.trim());
  }
  if (params.search && params.search.trim()) {
    queryParams.append('search', params.search.trim());
  }
  if (params.status && params.status !== 'ALL') {
    queryParams.append('status', params.status);
  }
  if (params.hotel) {
    queryParams.append('hotel', params.hotel);
  }
  // Multi-value filters → one repeated param per value
  (params.rank ?? []).forEach((r) => queryParams.append('rank', r));
  (params.crew_base ?? []).forEach((b) => queryParams.append('crew_base', b));
  if (params.crew_group) queryParams.append('crew_group', params.crew_group);
  (params.occupancy ?? []).forEach((o) => queryParams.append('occupancy', o));
  (params.operational_type ?? []).forEach((v) => queryParams.append('operational_type', v));

  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/bookings/?${queryString}` : '/api/v1/bookings/';

  const response = await get<PaginatedResponse<BookingBackendResponse>>(path);

  // Map results through FK field renamer + cost string→number converter
  return {
    count: response.count,
    next: response.next,
    previous: response.previous,
    results: response.results.map(mapBookingResponse),
  };
}

// =============================================================================
// Accruals wiring: Fetch bookings as AccrualEntry[] for accruals view
// =============================================================================

/**
 * Accrual bookings fetch params for server-side filtering.
 * Stage: Accruals wiring.
 */
export interface AccrualBookingsParams {
  accrual_stage?: string; // CONSUMED / ACCRUED / INVOICED / PAID / VOID
  accrual_stage__in?: string; // comma-separated, e.g. 'ACCRUED,INVOICED,PAID'
  invoice_approval_status?: string; // NOT_INVOICED / PENDING / APPROVED / REJECTED
  invoice_approval_status__in?: string; // comma-separated
  accrual_period?: string; // YYYY-MM
  aging_bucket?: string; // 0-7 / 8-30 / 31-60 / 60+
  hotel?: string; // UUID
  station_iata?: string; // IATA code
  accrual_checkout_date?: string; // YYYY-MM-DD
  accrual_checkout_date__gte?: string; // YYYY-MM-DD
  accrual_checkout_date__lte?: string; // YYYY-MM-DD
  ordering?: string; // accrual_checkout_date / expected_amount / aging_days / updated_at (prefix - for descending)
}

/**
 * Fetch all accrual-eligible bookings from backend as AccrualEntry[].
 * Stage: Accruals wiring (adapter pattern).
 *
 * Uses fetchAllPages to follow DRF pagination (PAGE_SIZE=50, 1,014 bookings = 21 pages).
 * Fetches bookings with accrual fields populated, then maps to AccrualEntry shape via adapter.
 *
 * @param params - Optional server-side filtering params (accrual_stage, accrual_period, aging_bucket, hotel, station_iata, date range, ordering)
 * @returns Array of AccrualEntry (adapted from Booking)
 *
 * CRITICAL:
 * - MUST use fetchAllPages to fetch ALL pages (1,014 bookings across 21 pages).
 *   A single call without fetchAllPages returns only 50 and silently drops 964.
 *   This exact bug happened in FW-7 piece A (projection entries: 50 of 88 returned, 5 stations missing).
 * - Maps each Booking through mapBookingResponse (FK renaming + decimal conversion) then mapBookingToAccrualEntry (adapter)
 * - Server-side filtering: backend supports accrual_stage, accrual_period, aging_bucket, hotel, station_iata, date range filters
 * - aging_bucket=60+ must be URL-encoded as 60%2B
 * - Invalid accrual_stage returns 400 error (not silently ignored)
 * - Returns empty array if no bookings match filter (no error)
 */
/**
 * Bookings the hotel has invoiced, as Booking rows rather than AccrualEntry.
 * The invoices view needs num_nights, contracted_rate, taxes and source_kind,
 * which the AccrualEntry adapter discards.
 */
export async function fetchBookingsRawForInvoices(): Promise<import('@/lib/types').Booking[]> {
  const raw = await fetchAllPages<BookingBackendResponse>(
    '/api/v1/bookings/?invoice_approval_status__in=PENDING,APPROVED,REJECTED'
  );
  return raw.map(mapBookingResponse);
}

export async function fetchAccrualBookings(
  params: AccrualBookingsParams = {}
): Promise<import('@/lib/types').AccrualEntry[]> {
  // Build query string with accrual-specific filters
  const queryParams = new URLSearchParams();

  if (params.accrual_stage && params.accrual_stage.trim()) {
    queryParams.append('accrual_stage', params.accrual_stage.trim());
  }
  if (params.accrual_stage__in && params.accrual_stage__in.trim()) {
    queryParams.append('accrual_stage__in', params.accrual_stage__in.trim());
  }
  if (params.invoice_approval_status && params.invoice_approval_status.trim()) {
    queryParams.append('invoice_approval_status', params.invoice_approval_status.trim());
  }
  if (params.invoice_approval_status__in && params.invoice_approval_status__in.trim()) {
    queryParams.append('invoice_approval_status__in', params.invoice_approval_status__in.trim());
  }
  if (params.accrual_period && params.accrual_period.trim()) {
    queryParams.append('accrual_period', params.accrual_period.trim());
  }
  if (params.aging_bucket && params.aging_bucket.trim()) {
    // URLSearchParams.append() already percent-encodes, don't double-encode
    queryParams.append('aging_bucket', params.aging_bucket.trim());
  }
  if (params.hotel && params.hotel.trim()) {
    queryParams.append('hotel', params.hotel.trim());
  }
  if (params.station_iata && params.station_iata.trim()) {
    queryParams.append('station_iata', params.station_iata.trim());
  }
  if (params.accrual_checkout_date && params.accrual_checkout_date.trim()) {
    queryParams.append('accrual_checkout_date', params.accrual_checkout_date.trim());
  }
  if (params.accrual_checkout_date__gte && params.accrual_checkout_date__gte.trim()) {
    queryParams.append('accrual_checkout_date__gte', params.accrual_checkout_date__gte.trim());
  }
  if (params.accrual_checkout_date__lte && params.accrual_checkout_date__lte.trim()) {
    queryParams.append('accrual_checkout_date__lte', params.accrual_checkout_date__lte.trim());
  }
  if (params.ordering && params.ordering.trim()) {
    queryParams.append('ordering', params.ordering.trim());
  }

  // page_size is NOT appended here — fetchAllPages owns it exclusively and
  // applies its own default (1000), so 21 pages at the backend's default 50
  // collapses to ~1-2 requests.
  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/bookings/?${queryString}` : '/api/v1/bookings/';

  // Fetch ALL pages (backend returns 1000/page with page_size=1000, 1,014 bookings total = 2 pages)
  const rawBookings = await fetchAllPages<BookingBackendResponse>(path);

  // Map each booking through FK renamer + cost/accrual decimal converter, then through AccrualEntry adapter
  return rawBookings.map(mapBookingResponse).map(mapBookingToAccrualEntry);
}

// =============================================================================
// AB-2 frontend: Audit Log backend wiring
// =============================================================================

/**
 * Backend AuditLogEntry response with actor_user as INTEGER.
 * Backend serializes actor_user FK as INTEGER (e.g., 1), not a UUID string.
 * before_json and after_json are already string|null — no mapping needed.
 */
interface AuditLogEntryBackendResponse {
  id: string;
  actor_user: number; // INTEGER (not string UUID)
  action: import('@/lib/types').AuditAction;
  entity_type: string;
  entity_id: string;
  before_json: string | null; // Pass through (already string|null)
  after_json: string | null; // Pass through (already string|null)
  request_id: string;
  prev_row_hash: string | null;
  row_hash: string;
  created_at: string;
  // Backend may return updated_at — drop it (not in frontend type)
  updated_at?: string;
}

/**
 * Pure mapper: converts backend audit log entry response to frontend AuditLogEntry type.
 *
 * CRITICAL transformations:
 * - actor_user (number) → actor_user_id (string via String())
 * - before_json/after_json pass through unchanged (already string|null)
 * - All other fields (action, entity_type, entity_id, request_id, prev_row_hash, row_hash, created_at) pass through
 * - Drop updated_at (not in frontend type)
 */
function mapAuditLogEntryResponse(raw: AuditLogEntryBackendResponse): import('@/lib/types').AuditLogEntry {
  const { actor_user, updated_at, ...rest } = raw;

  return {
    ...rest,
    // actor_user (number) → actor_user_id (string)
    actor_user_id: String(actor_user),
    // before_json/after_json/action/entity_type/entity_id/request_id/prev_row_hash/row_hash/created_at pass through
  };
}

/**
 * Fetch all audit log entries from backend.
 * Stage AB-2 frontend (read-only).
 *
 * Uses fetchAllPages to follow DRF pagination (PAGE_SIZE=50, count 91 = 2 pages).
 * Applies mapAuditLogEntryResponse to convert actor_user number to actor_user_id string.
 *
 * @param date - Optional date string (YYYY-MM-DD) to filter entries for a specific day
 * @returns Array of AuditLogEntry (mapped to frontend shape)
 *
 * CRITICAL:
 * - If date provided: uses backend ?date= filter to fetch only that day's entries (server-side scoping)
 * - If date omitted: fetches ALL audit log entries across all pages (~11,451 total — heavy)
 * - Uses fetchAllPages to follow DRF `next` links (pagination preserves query params)
 * - Maps each result through mapAuditLogEntryResponse
 * - Does NOT touch s.auditLog (10 consumers + mock write path preserved)
 */
export async function fetchAuditLog(date?: string): Promise<import('@/lib/types').AuditLogEntry[]> {
  // Build query params with optional date filter. page_size is NOT appended
  // here — fetchAllPages owns it exclusively and applies its own default (1000).
  const queryParams = new URLSearchParams();
  if (date) {
    queryParams.append('date', date);
  }

  const queryString = queryParams.toString();
  const path = queryString ? `/api/v1/audit-log-entries/?${queryString}` : '/api/v1/audit-log-entries/';

  // Fetch ALL pages (backend returns 1000/page with page_size=1000)
  const rawEntries = await fetchAllPages<AuditLogEntryBackendResponse>(path);

  // Map results through FK field renamer (actor_user number → actor_user_id string)
  return rawEntries.map(mapAuditLogEntryResponse);
}

/**
 * Fetch the most recent N audit log entries from backend.
 * Stage AB-2 frontend (efficient recent-mode for ActivityRail).
 *
 * Uses backend ?recent=N query param to fetch last N entries in reverse-chronological order.
 * Efficient: avoids paging through all ~11,451 entries.
 * Applies mapAuditLogEntryResponse to convert actor_user number to actor_user_id string.
 *
 * @param n - Number of recent entries to fetch (default 20, capped at 100 on backend)
 * @returns Array of AuditLogEntry (mapped to frontend shape, newest first)
 *
 * CRITICAL:
 * - Backend returns newest-first for ?recent=N (reverse-chronological order)
 * - Single request (N is small, no pagination needed)
 * - Maps each result through mapAuditLogEntryResponse
 * - Returns entries in newest-first order (do NOT reverse)
 */
export async function fetchRecentAuditLog(n: number = 20): Promise<import('@/lib/types').AuditLogEntry[]> {
  // Backend returns newest-first for ?recent; a single request (N is small), no pagination needed.
  const raw = await get<PaginatedResponse<AuditLogEntryBackendResponse>>(
    `/api/v1/audit-log-entries/?recent=${n}`
  );

  // Backend returns paginated envelope with results array
  const list = raw.results ?? [];

  // Map results through FK field renamer (actor_user number → actor_user_id string)
  return list.map(mapAuditLogEntryResponse);
}

/**
 * Backend verify response with snake_case field names.
 * Frontend VerifyAuditChainResult (lib/store/phase3-actions.ts) expects camelCase.
 */
interface AuditVerifyBackendResponse {
  ok: boolean;
  total_checked: number;
  first_break_id: string | null;
  reason: string | null;
  per_row: Array<{ id: string; ok: boolean }>;
}

/**
 * Fetch audit chain verification result from backend.
 * Stage AB-2 frontend.
 *
 * Calls backend GET /api/v1/audit-log-entries/verify/ and maps snake_case → camelCase.
 *
 * @returns VerifyAuditChainResult (camelCase field names)
 *
 * CRITICAL:
 * - Maps total_checked → totalChecked
 * - Maps first_break_id → firstBreakAt
 * - Maps per_row → perRow
 * - ok and reason pass through unchanged
 */
export async function fetchAuditVerify(): Promise<import('@/lib/store/phase3-actions').VerifyAuditChainResult> {
  const response = await get<AuditVerifyBackendResponse>('/api/v1/audit-log-entries/verify/');

  // Map snake_case → camelCase
  return {
    ok: response.ok,
    totalChecked: response.total_checked,
    firstBreakAt: response.first_break_id,
    reason: response.reason,
    perRow: response.per_row,
  };
}

// =============================================================================
// D-4 trigger: send-to-hotel workflow
// =============================================================================

export interface TriggerD4Response {
  task_dispatched: boolean;
  target_date: string;
}

export async function triggerD4Allocator(targetDate: string): Promise<TriggerD4Response> {
  return post<TriggerD4Response>('/api/v1/projection-runs/trigger-d4/', { target_date: targetDate });
}

/**
 * Apply an Etihad roster-change event. The backend edits the roster for the
 * target date and the booking delta follows (create / patch / cancel). Returns
 * the handler result (what changed). See apps/events/handlers_etihad.py.
 */
export interface ApplyRosterEventResponse {
  ok: boolean;
  type: string;
  result: Record<string, unknown>;
}
export async function applyRosterEvent(
  type: string,
  payload: Record<string, unknown>,
): Promise<ApplyRosterEventResponse> {
  return post<ApplyRosterEventResponse>('/api/v1/events/apply/', { type, payload });
}

// =============================================================================
// Ops projection approval: approve/modify/reject workflow
// =============================================================================

/**
 * Backend response shape for reject/modify actions.
 * Returns the updated entry + any cascaded entries + any new unallotted report.
 */
interface ProjectionResponseWithCascade {
  entry: ProjectionEntryBackendResponse;
  cascaded: ProjectionEntryBackendResponse[];
  unallotted: UnallottedReportBackendResponse | null;
}

/**
 * Frontend response shape for reject/modify actions.
 * Maps backend response to frontend types.
 */
export interface ProjectionResponseWithCascadeMapped {
  entry: import('@/lib/types').ProjectionEntry;
  cascaded: import('@/lib/types').ProjectionEntry[];
  unallotted: UnallottedReport | null;
}

export async function approveProjectionEntry(id: string, notes = '') {
  return post(`/api/v1/projection-entries/${id}/approve/`, { notes });
}

export async function modifyProjectionEntry(id: string, rooms_offered: number, notes = ''): Promise<ProjectionResponseWithCascadeMapped> {
  const response = await post<ProjectionResponseWithCascade>(`/api/v1/projection-entries/${id}/modify/`, { rooms_offered, notes });

  return {
    entry: mapProjectionEntryResponse(response.entry),
    cascaded: response.cascaded.map(mapProjectionEntryResponse),
    unallotted: response.unallotted ? mapUnallottedReportResponse(response.unallotted) : null,
  };
}

export async function rejectProjectionEntry(id: string, notes = ''): Promise<ProjectionResponseWithCascadeMapped> {
  const response = await post<ProjectionResponseWithCascade>(`/api/v1/projection-entries/${id}/reject/`, { notes });

  return {
    entry: mapProjectionEntryResponse(response.entry),
    cascaded: response.cascaded.map(mapProjectionEntryResponse),
    unallotted: response.unallotted ? mapUnallottedReportResponse(response.unallotted) : null,
  };
}

// =============================================================================
// Bulk approve: approve all projected entries for a run
// =============================================================================

export interface BulkApproveResponse {
  approved: number;
  run_id: string;
  failed: { id: string; status: string; reason: string }[];
}

/** Server-side count of what a bulk action would touch; nothing changes. */
export interface ProjectionBulkDryRun {
  count: number;
  dry_run: true;
}

export async function bulkApproveProjection(
  runId: string,
  scope: ProjectionBulkScope = {},
): Promise<BulkApproveResponse> {
  return post('/api/v1/projection-entries/bulk-approve/', { run_id: runId, ...scope });
}

export async function dryRunBulkApproveProjection(
  runId: string,
  scope: ProjectionBulkScope,
): Promise<ProjectionBulkDryRun> {
  return post('/api/v1/projection-entries/bulk-approve/', { run_id: runId, ...scope, dry_run: true });
}

// =============================================================================
// Inventory alert acknowledge/release workflow
// =============================================================================

export async function acknowledgeAlert(id: string, notes = '') {
  return post(`/api/v1/inventory-alerts/${id}/acknowledge/`, { notes });
}

export async function releaseAlertRooms(id: string, notes = '') {
  return post(`/api/v1/inventory-alerts/${id}/release/`, { notes });
}

// =============================================================================
// Ad-hoc hotel assignment workflow
// =============================================================================

export interface AdHocAssignInput {
  hotel_id: string;
  unallotted_id: string;
  date_local: string;
  rooms_needed: number;
  notes: string;
}

export async function assignAdHocHotel(runId: string, input: AdHocAssignInput) {
  return post(`/api/v1/projection-runs/${runId}/assign-adhoc/`, input);
}

/**
 * Resolve an unallotted report by recording the hotel assignment.
 * POST /api/v1/unallotted-reports/{id}/resolve/
 *
 * @param id - UnallottedReport UUID
 * @param hotelId - Hotel UUID that was assigned
 * @param notes - Optional resolution notes
 * @returns The updated UnallottedReport with resolution fields populated
 */
export async function resolveUnallottedReport(
  id: string,
  hotelId: string,
  notes?: string
): Promise<UnallottedReport> {
  const raw = await post<UnallottedReportBackendResponse>(
    `/api/v1/unallotted-reports/${id}/resolve/`,
    { hotel: hotelId, notes: notes || '' }
  );
  return mapUnallottedReportResponse(raw);
}

export async function sendAllProjectionsToHotels(
  runId: string,
  scope: ProjectionBulkScope = {},
): Promise<{ count: number }> {
  return post(`/api/v1/projection-runs/${runId}/send-all-to-hotels/`, scope);
}

export async function dryRunSendProjectionsToHotels(
  runId: string,
  scope: ProjectionBulkScope,
): Promise<ProjectionBulkDryRun> {
  return post(`/api/v1/projection-runs/${runId}/send-all-to-hotels/`, { ...scope, dry_run: true });
}

// =============================================================================
// Generate Projection: async allocator dispatch
// =============================================================================

export interface GenerateProjectionResponse {
  task_dispatched: boolean;
  roster_month: string;
  publication_id: string;
}

export async function generateProjection(rosterMonth?: string): Promise<GenerateProjectionResponse> {
  return post('/api/v1/projection-runs/generate-projection/', rosterMonth ? { roster_month: rosterMonth } : {});
}

// =============================================================================
// Hotel approval workflow: approve/reject booking by hotel
// =============================================================================

export async function approveBookingByHotel(id: string): Promise<import('@/lib/types').Booking> {
  const raw = await post<BookingBackendResponse>(`/api/v1/bookings/${id}/hotel-approve/`, {});
  return mapBookingResponse(raw);
}

export async function rejectBookingByHotel(id: string): Promise<import('@/lib/types').Booking> {
  const raw = await post<BookingBackendResponse>(`/api/v1/bookings/${id}/hotel-reject/`, {});
  return mapBookingResponse(raw);
}

// =============================================================================
// Check-in/Check-out workflow: OTP verification with backend transition
// =============================================================================

export async function checkInBooking(id: string): Promise<import('@/lib/types').Booking> {
  const raw = await post<BookingBackendResponse>(`/api/v1/bookings/${id}/check-in/`, {});
  return mapBookingResponse(raw);
}

export async function checkOutBooking(id: string): Promise<import('@/lib/types').Booking> {
  const raw = await post<BookingBackendResponse>(`/api/v1/bookings/${id}/check-out/`, {});
  return mapBookingResponse(raw);
}

/** POST /bookings/{id}/verify-checkin-otp/ (or -checkout-): the backend checks
 * the code it sent the crew and, on a match, records who confirmed and moves
 * the booking to CHECKED_IN / CHECKED_OUT. A wrong code is a 400. */
export async function verifyBookingOtp(
  id: string,
  direction: 'IN' | 'OUT',
  otpCode: string,
): Promise<void> {
  const path = direction === 'IN' ? 'verify-checkin-otp' : 'verify-checkout-otp';
  await post<unknown>(`/api/v1/bookings/${encodeURIComponent(id)}/${path}/`, { otp_code: otpCode });
}

/** What automatic D-N booking actually does today: whether its beat entry is
 * scheduled, and the lead days the task uses. Read-only. */
export type BookingRunKey = 'D48' | 'D24' | 'D0';
export type BookingRunMode = 'times' | 'every';

/** One scheduled booking run (GET /api/v1/booking-schedules/): when it runs,
 * in `timezone`. mode 'times' -> `times` ("HH:MM"); 'every' -> `every_hours`. */
export interface BookingSchedule {
  key: BookingRunKey;
  label: string;
  lead_days: number;
  enabled: boolean;
  /** false: no beat row yet (the defaults are shown). */
  scheduled: boolean;
  mode: BookingRunMode;
  times: string[];
  every_hours: number | null;
  timezone: string;
  last_run_at: string | null;
}

export type BookingScheduleChanges = Partial<Pick<BookingSchedule, 'enabled' | 'mode' | 'times' | 'every_hours'>>;

export async function fetchBookingSchedules(): Promise<BookingSchedule[]> {
  return get<BookingSchedule[]>('/api/v1/booking-schedules/');
}

/** SUPER_ADMIN only; audit-logged. A 400 carries a user-facing `detail`. */
export async function updateBookingSchedule(key: BookingRunKey, changes: BookingScheduleChanges): Promise<BookingSchedule> {
  return patch<BookingSchedule>(`/api/v1/booking-schedules/${key}/`, changes);
}

/** One allotment-matrix row (GET /api/v1/allotment-rules/, read-only). */
export interface AllotmentRule {
  id: string;
  station_iata: string;
  hotel: string;
  hotel_name: string;
  crew_group: string;
  crew_group_name: string;
  priority: number;
  priority_label: string;
  start_date: string | null;
  end_date: string | null;
  active: boolean;
  /** Active any-crew rule: what the allocator actually reads. */
  used_by_allocator: boolean;
}

export async function fetchAllotmentRules(): Promise<AllotmentRule[]> {
  return fetchAllPages<AllotmentRule>('/api/v1/allotment-rules/', 100);
}
